import type { ReactNode } from "react";

import { classes } from "./classes.js";

/**
 * `P0-UIK-001`: columns and rows, generic over the row.
 *
 * `cell` returns a `ReactNode` rather than a string, which is what lets a
 * money column pass `<Money>` and a date column pass `<DateText>` instead of
 * formatting inside the table , the same reason `StatCard` takes a node.
 */
export interface Column<Row> {
  readonly key: string;
  readonly header: ReactNode;
  readonly cell: (row: Row) => ReactNode;
  /** `text-right` for a figure column, so amounts align with `tabular-nums`. */
  readonly align?: "left" | "right";
}

export interface TableProps<Row> {
  readonly columns: readonly Column<Row>[];
  readonly rows: readonly Row[];
  readonly rowKey: (row: Row) => string;
  readonly empty?: ReactNode;
  /**
   * `F-0b`: **a row that opens something** (the bank's pipeline and queues). The row is `role="link"`, focusable and
   * opened by Enter as well as by a click, and it is NAMED (`label`), because a row whose name is the
   * concatenation of its cells tells a screen reader nothing about where it goes. Absent, rows are plain rows.
   */
  readonly rowLink?: {
    readonly onOpen: (row: Row) => void;
    readonly label: (row: Row) => string;
  };
  readonly className?: string;
  /** A `<tfoot>` row the caller draws (cells it spans itself), under the rows: a total, a footnote line. */
  readonly footer?: ReactNode;
  /** The table runs edge to edge in its card: the bank's skin pads its first and last cell at the card's 20 px. */
  readonly flush?: boolean;
}

export function Table<Row>({
  columns,
  rows,
  rowKey,
  empty,
  rowLink,
  className,
  footer,
  flush,
}: TableProps<Row>): React.JSX.Element {
  if (rows.length === 0 && empty !== undefined) return <>{empty}</>;
  return (
    <table
      data-kit="table"
      {...(flush === true ? { "data-flush": "" } : {})}
      className={classes("w-full text-sm", className)}
    >
      <thead>
        <tr className="border-b border-gray-200 text-xs text-gray-500">
          {columns.map((column) => (
            <th
              key={column.key}
              scope="col"
              className={classes("py-2 font-medium", column.align === "right" ? "text-right" : "text-left")}
            >
              {column.header}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr
            key={rowKey(row)}
            className="border-b border-gray-100"
            {...(rowLink === undefined
              ? {}
              : {
                  role: "link",
                  tabIndex: 0,
                  "aria-label": rowLink.label(row),
                  "data-kit": "row-link",
                  onClick: () => {
                    rowLink.onOpen(row);
                  },
                  onKeyDown: (event: React.KeyboardEvent) => {
                    if (event.key !== "Enter" || event.target !== event.currentTarget) return;
                    event.preventDefault();
                    rowLink.onOpen(row);
                  },
                })}
          >
            {columns.map((column) => (
              <td
                key={column.key}
                className={classes(
                  "py-3 text-gray-900",
                  column.align === "right" ? "text-right" : "text-left",
                )}
              >
                {column.cell(row)}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
      {footer === undefined ? null : <tfoot>{footer}</tfoot>}
    </table>
  );
}
